import { Button } from 'antd';
import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { SizeCode } from 'entities/SizeCode';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { PIXELS_PER_METER } from '../../model/constants/mapScale';
import { BoxShape } from '../../model/types';
import { getNearestSizeCode } from '../../utils/helpers/getNearestSizeCode';
import { getShapeSquareMeters } from '../../utils/helpers/shapeGeometry';

interface SizeCodeSquareInfoProps {
  shape: BoxShape;
  availableSizeCodes: SizeCode[];
  onReset: (sizeCodeId: string) => void;
}

export const SizeCodeSquareInfo: FC<SizeCodeSquareInfoProps> = memo(({ shape, availableSizeCodes, onReset }) => {
  const { t } = useAppTranslation('warehouse-management');

  const shapeSquare = useMemo(() => getShapeSquareMeters(shape, PIXELS_PER_METER), [shape]);

  const current = useMemo(
    () => availableSizeCodes.find((code) => code.sizeCodeId === shape.sizeCodeId),
    [availableSizeCodes, shape.sizeCodeId],
  );

  const nearest = useMemo(() => {
    if (shapeSquare <= 0) return undefined;
    const sidePx = Math.sqrt(shapeSquare) * PIXELS_PER_METER;
    return getNearestSizeCode({
      shapeWidth: sidePx,
      shapeLength: sidePx,
      availableSizeCodes,
      pixelsPerMeter: PIXELS_PER_METER,
    });
  }, [shapeSquare, availableSizeCodes]);

  const canReset = Boolean(nearest && nearest.sizeCodeId !== shape.sizeCodeId);

  return (
    <div className="mt-1 text-xs">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-primaryLight">{t('Shape')}</span>
        <span className="text-primary">{roundNumber(shapeSquare, 2)} m²</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-primaryLight">{t('Size code')}</span>
        <span className="text-primary">{current ? `${roundNumber(current.square, 2)} m²` : '—'}</span>
      </div>
      {canReset && (
        <div className="flex justify-end">
          <Button
            type="link"
            size="small"
            className="!p-0 !h-auto"
            onClick={() => {
              if (nearest) onReset(nearest.sizeCodeId);
            }}
          >
            {t('Reset to nearest')}
          </Button>
        </div>
      )}
    </div>
  );
});
